import { queryClient } from '../../../ReactQuery';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { put } from '../../../redux/api';
import { getTeamGroupQueryKey } from '../teams/teamGroups/useTeamGroup';
import { getBaseTeamMembersQueryKey } from '../teams/useTeamMembers';
import type { FullTeamGroupResponse } from '../../../../shared/types/routes/teamGroups';
import type { SetUserTeamRolesRequest } from '../../../../typings/api';

type UpdateUserTeamRolesRequest = {
  userId: number;
  newRoles: SetUserTeamRolesRequest;
};

//  Function to update a user's team roles.
function updateUserTeamRoles({ userId, newRoles }: UpdateUserTeamRolesRequest) {
  return put<SetUserTeamRolesRequest, void>(`/api/user/${userId}/team-roles`, newRoles);
}

/**
 * Success handler for when this is used in the context of a team group.
 */
function onUpdateTeamGroupRolesSuccess(teamGroupId: number, { userId, newRoles }: UpdateUserTeamRolesRequest) {
  //  We can safely do this, because we only ever use this to update a single role at a time.
  const [newRole] = newRoles;
  //  Update the user roles in the cache
  queryClient.setQueryData(
    getTeamGroupQueryKey(teamGroupId),
    (teamGroup?: FullTeamGroupResponse): FullTeamGroupResponse | undefined => {
      if (!teamGroup) {
        return teamGroup;
      }
      //  Mostly for type safety, but also to avoid unnecessary updates.
      if (!newRole?.roleId) {
        return teamGroup;
      }

      const newMembers = teamGroup.members.map((member) => {
        if (member.userId === userId) {
          return {
            ...member,
            roleId: newRole.roleId,
          };
        }
        return member;
      });
      return {
        ...teamGroup,
        members: newMembers,
      };
    },
  );
  //  The team group query is invalidated, so that the new team group data is fetched.
  queryClient.invalidateQueries(getTeamGroupQueryKey(teamGroupId));
}

/**
 * useUpdateUserTeamRoles is a hook that updates the roles of a user on a team.
 */
export function useUpdateUserTeamGroupRoles(teamId: number) {
  return useToastedMutation({
    mutationFn: updateUserTeamRoles,
    successToastMessage: `The user's team role has been updated successfully.`,
    errorToastMessage: `Failed to update the user's team role.`,
    onSuccess: (_response, { userId, newRoles }: UpdateUserTeamRolesRequest) => {
      onUpdateTeamGroupRolesSuccess(teamId, { userId, newRoles });
    },
  });
}

/**
 * Success handler for when this is used in the context of a team group.
 */
function onUpdateTeamRolesSuccess(teamId: number) {
  // //  The team group query is invalidated, so that the new team group data is fetched.
  queryClient.invalidateQueries(getBaseTeamMembersQueryKey(teamId));
}

/**
 * useUpdateUserTeamRoles is a hook that updates the roles of a user on a team.
 */
export default function useUpdateUserTeamRoles(teamId: number) {
  return useToastedMutation({
    mutationFn: updateUserTeamRoles,
    successToastMessage: `The user's team role has been updated successfully.`,
    errorToastMessage: `Failed to update the user's team role.`,
    onSuccess: () => {
      onUpdateTeamRolesSuccess(teamId);
    },
  });
}
